/*
  <quick-add>: the size sheet behind the + button on product cards (snippets/quick-add-drawer.liquid).
  Loads the piece from /products/<handle>.js, lists its sizes (sold-out ones crossed out) and adds the
  chosen size to the cart, or sends it on WhatsApp. Nothing is pre-selected.
*/
const Spectre = window.Spectre || {};
const rootUrl = () => {
  const root = Spectre.routes?.root || '/';
  return root.endsWith('/') ? root : `${root}/`;
};
const splitTitle = (title) => {
  const parts = String(title || '').split(' - ');
  if (parts.length < 2) return { name: title || '', color: '' };
  const color = parts.pop().trim();
  return { name: parts.join(' - ').trim(), color };
};

class QuickAdd extends HTMLElement {
  connectedCallback() {
    this.drawer = this.closest('spectre-drawer');
    this.submit = this.querySelector('[data-quick-submit]');
    this.submitLabel = this.querySelector('[data-quick-submit-label]');
    this.addEventListener('change', this.onChange);
    this.submit?.addEventListener('click', this.onSubmit);
  }

  async show(trigger) {
    const handle = trigger.dataset.quickOpen;
    this.handle = handle;
    this.product = null;
    this.variant = null;
    this.renderTitle(trigger.dataset.quickTitle || '');
    this.renderImage(trigger.dataset.quickImage);
    this.querySelector('[data-quick-price]').textContent = '';
    this.querySelector('[data-quick-sizes]').replaceChildren();
    this.querySelector('[data-quick-selected]').textContent = '';
    this.querySelector('[data-quick-loading]').hidden = false;
    this.querySelector('[data-quick-hint]').hidden = true;
    this.querySelector('[data-quick-success]').hidden = true;
    this.querySelector('[data-quick-link]').href = `${rootUrl()}products/${encodeURIComponent(handle)}`;
    this.hideError();
    this.updateButton();
    this.drawer?.open(trigger);

    try {
      const response = await fetch(`${rootUrl()}products/${encodeURIComponent(handle)}.js`, { headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error(String(response.status));
      const product = await response.json();
      if (this.handle !== handle) return;
      this.product = product;
      this.render();
    } catch (error) {
      if (this.handle !== handle) return;
      this.querySelector('[data-quick-loading]').hidden = true;
      this.showError(Spectre.strings?.quickError);
    }
  }

  renderTitle(title) {
    const { name, color } = splitTitle(title);
    const target = this.querySelector('[data-quick-name]');
    const nameNode = document.createElement('span');
    nameNode.className = 'ptitle__name';
    nameNode.textContent = name;
    const parts = [nameNode];
    if (color) {
      const colorNode = document.createElement('span');
      colorNode.className = 'ptitle__color';
      colorNode.textContent = color;
      parts.push(document.createTextNode(' '), colorNode);
    }
    target.replaceChildren(...parts);
  }

  renderImage(src) {
    const media = this.querySelector('[data-quick-media]');
    media.replaceChildren();
    if (!src) return;
    const image = document.createElement('img');
    image.src = src;
    image.alt = '';
    image.width = 240;
    image.height = 240;
    media.append(image);
  }

  render() {
    const product = this.product;
    this.renderTitle(product.title);
    const image = product.featured_image ? `${product.featured_image}${product.featured_image.includes('?') ? '&' : '?'}width=240` : '';
    if (image) this.renderImage(image);
    this.querySelector('[data-quick-link]').href = product.url || this.querySelector('[data-quick-link]').href;
    this.renderPrice();

    const name = `quick-size-${product.id}`;
    const nodes = [];
    product.variants.forEach((variant) => {
      const id = `${name}-${variant.id}`;
      const input = document.createElement('input');
      input.type = 'radio';
      input.className = 'visually-hidden quick-add__input';
      input.name = name;
      input.id = id;
      input.value = variant.id;
      input.disabled = !variant.available;
      const label = document.createElement('label');
      label.htmlFor = id;
      label.className = `quick-add__size${variant.available ? '' : ' is-unavailable'}`;
      label.textContent = variant.title;
      if (!variant.available) {
        const status = document.createElement('span');
        status.className = 'visually-hidden';
        status.textContent = `, ${Spectre.strings?.soldOut || ''}`;
        label.append(status);
      }
      nodes.push(input, label);
    });
    this.querySelector('[data-quick-sizes]').replaceChildren(...nodes);
    this.querySelector('[data-quick-loading]').hidden = true;
    this.querySelector('[data-quick-hint]').hidden = !product.variants.some((variant) => /\d\s*cm/i.test(variant.title));
    this.updateButton();
    this.updateWhatsApp();
  }

  renderPrice() {
    const price = this.querySelector('[data-quick-price]');
    const product = this.product;
    if (!product) return;
    const cents = this.variant ? this.variant.price : product.price;
    const compare = this.variant ? this.variant.compare_at_price : product.compare_at_price;
    let text = Spectre.formatMoney(cents);
    if (!this.variant && product.price_varies && Spectre.strings?.priceFrom) text = Spectre.strings.priceFrom.replace('[price]', text);
    price.replaceChildren(document.createTextNode(text));
    if (compare && compare > cents) {
      const old = document.createElement('s');
      old.className = 'price__compare';
      old.textContent = Spectre.formatMoney(compare);
      price.append(' ', old);
    }
  }

  onChange = (event) => {
    if (!event.target.matches('.quick-add__input') || !this.product) return;
    this.variant = this.product.variants.find((variant) => String(variant.id) === event.target.value) || null;
    this.querySelector('[data-quick-selected]').textContent = this.variant ? this.variant.title : '';
    this.hideError();
    this.querySelector('[data-quick-success]').hidden = true;
    this.renderPrice();
    this.updateButton();
    this.updateWhatsApp();
  };

  updateButton() {
    if (!this.submit) return;
    const strings = Spectre.strings || {};
    let label = strings.chooseSizeFirst || strings.chooseSize;
    let disabled = true;
    if (this.variant) {
      disabled = !this.variant.available;
      label = this.variant.available ? strings.addToCart : strings.soldOut;
    }
    this.submit.disabled = disabled;
    if (this.submitLabel) this.submitLabel.textContent = label;
  }

  updateWhatsApp() {
    const link = this.querySelector('[data-quick-whatsapp]')?.closest('whatsapp-link');
    if (!link?.setTokens || !this.product) return;
    const variantTitle = this.variant && this.variant.title !== 'Default Title' ? this.variant.title : '';
    const path = this.variant ? `${this.product.url}?variant=${this.variant.id}` : this.product.url;
    link.dataset.variantId = this.variant ? this.variant.id : '';
    link.setTokens({
      product: this.product.title,
      variant: variantTitle,
      qty: '1',
      price: Spectre.formatMoney(this.variant ? this.variant.price : this.product.price),
      url: `${window.location.origin}${path}`,
    });
  }

  showError(message) {
    const error = this.querySelector('[data-quick-error]');
    if (!error || !message) return;
    error.textContent = message;
    error.hidden = false;
    Spectre.announce?.(message);
  }

  hideError() {
    this.querySelector('[data-quick-error]')?.setAttribute('hidden', '');
  }

  onSubmit = async () => {
    if (!this.variant || !this.variant.available || this.submit.getAttribute('aria-busy') === 'true') return;
    this.hideError();
    this.submit.setAttribute('aria-busy', 'true');
    const previous = this.submitLabel?.textContent;
    if (this.submitLabel) this.submitLabel.textContent = Spectre.strings?.adding || previous;
    try {
      await Spectre.cart.add({ items: [{ id: this.variant.id, quantity: 1 }] }, 'quick-add');
      Spectre.announce?.(Spectre.strings?.added);
      if (Spectre.cart.sections().length) this.drawer?.close();
      else this.querySelector('[data-quick-success]').hidden = false;
    } catch (error) {
      this.showError(error.description || Spectre.strings?.addError);
    } finally {
      this.submit.removeAttribute('aria-busy');
      this.updateButton();
    }
  };
}
if (!customElements.get('quick-add')) customElements.define('quick-add', QuickAdd);

document.addEventListener('click', (event) => {
  const trigger = event.target.closest('[data-quick-open]');
  if (!trigger) return;
  const sheet = document.querySelector('quick-add');
  event.preventDefault();
  if (sheet?.show) sheet.show(trigger);
  else window.location.href = `${rootUrl()}products/${encodeURIComponent(trigger.dataset.quickOpen)}`;
});
